import { useCallback, useEffect } from "react"
import { Outlet, useLocation, useMatches, useNavigate } from "react-router-dom"
import PropTypes from "prop-types"
import { chain, get, isEmpty, omit } from "lodash"
import Modal from "components/modal"
import StaticAlert from "modules/static-alert"
import SecurityProfile from "modules/security-profile"
import FactorProfile from "modules/factor-profile"
import Notifications from "modules/notifications"
import Dialog from "modules/dialog"
import { DATE } from "helpers/shapes"
import saveRecentResources from "hocs/saveRecentResources"
import withPreferenceContext from "hocs/withPreferenceContext"
import useMountEffect from "hooks/mount-effect"
import { useShareMyViewSub } from "hooks/share-my-view"
import { getQueryStringState, stringifyState } from "helpers/queryString"
import { LINK, SUB_LINK, CONTROLS, PORTFOLIO } from "./shapes"
import Nav from "./components/nav"
import Subnav from "./components/subnav"

import "./style.scss"

function PortfolioRoot({
  account,
  portfolioBenchmark,
  portfolioName,
  modelId,
  modelName,
  links,
  subLinks = [],
  controls,
  security,
  factorId,
  sidebar,
  latestPortfolios,
  closeFactorProfile,
  closeSecurityProfile,
  isActive,
  selectedBase,
  setSelectedBase,
  searchValue,
  baseInputFocus,
  baseHistory,
  searchResults,
  baseInputBlur,
  baseSearch,
  baseDropdownVisible,
  defaultSelectedBase,
  portfolioHasData,
  navigationDisabled,
  modifiedScreens,
  openDialog,
  openCompositeSecurity,
  portfolioId,
  noRestrictionModels,
  sessionExpired,
  portfolioDates,
  menuItems,
  modelSupportsComposites,
  unsupportedCurrencyWarning,
  dispatch,
  setModel,
  hasEnoughBaseCoverage,
  coverageNotification,
  loadActiveBase,
  updateStateFromQueryString,
}) {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!portfolioHasData) {
      navigate(`/${account}/p/${portfolioId}/upload`)
    }
  }, [navigate, portfolioHasData, account, portfolioId])

  useMountEffect(() => {
    const parsedSearch = getQueryStringState()
    if (!isEmpty(parsedSearch)) {
      updateStateFromQueryString(parsedSearch, modelId, account)
    }
    // We display this message ONLY if we mount and active is already on AND there's no base specified in the URL
    // Otherwise, it'll get displayed by the active toggle or the setting of the base
    if (isActive && !parsedSearch.base) {
      loadActiveBase()
    }

    const stringifiedSearch = stringifyState(omit(parsedSearch, ["modelId", "base"]))
    if (stringifiedSearch) {
      navigate(`${location.pathname}${stringifiedSearch ? `?${stringifiedSearch}` : ""}`, { replace: true })
    }
  })

  const onSharePropsRequested = useCallback(() => {
    return { modelId, base: isActive ? selectedBase : null }
  }, [modelId, selectedBase, isActive])
  useShareMyViewSub("portfolio-root", onSharePropsRequested)

  useEffect(() => {
    if (isActive && !hasEnoughBaseCoverage) {
      coverageNotification()
    }
  }, [coverageNotification, hasEnoughBaseCoverage, isActive])

  const matches = useMatches()
  const noContainer = chain(matches)
    .filter(match => get(match, "handle.noContainer"))
    .last()
    .get("handle.noContainer", false)
    .value()

  const handleSetModel = (modelId) => {
    setModel(modelId)
  }

  if (!portfolioHasData) {
    return null
  }

  return <div className="modules_portfolio-root">
    <Nav
      account={account}
      modelId={modelId}
      navigationDisabled={navigationDisabled}
      links={links}
      portfolioName={portfolioName}
      latestPortfolios={latestPortfolios}
      menuItems={menuItems}
    />
    <Subnav
      setModel={handleSetModel}
      subLinks={subLinks}
      portfolioBenchmark={portfolioBenchmark}
      isActiveViewType={isActive}
      controls={controls}
      setSelectedBase={setSelectedBase}
      selectedBase={selectedBase}
      modelId={modelId}
      modelName={modelName}
      searchValue={searchValue}
      baseInputFocus={baseInputFocus}
      baseDropdownVisible={baseDropdownVisible}
      baseHistory={baseHistory}
      searchResults={searchResults}
      baseInputBlur={baseInputBlur}
      baseSearch={baseSearch}
      defaultSelectedBase={defaultSelectedBase}
      modifiedScreens={modifiedScreens}
      openDialog={openDialog}
      openCompositeSecurity={openCompositeSecurity}
      portfolioId={portfolioId}
      noRestrictionModels={noRestrictionModels}
      portfolioDates={portfolioDates}
      modelSupportsComposites={modelSupportsComposites}
      unsupportedCurrencyWarning={unsupportedCurrencyWarning}
      dispatch={dispatch}
    />
    <div className="content">
      {sidebar}
      {noContainer ? <Outlet />
        : <div className="main">
          <div className="container-fluid">
            <StaticAlert />
            <Outlet />
          </div>
        </div>}
    </div>
    <Notifications />
    {security && <Modal superModalOpened={sessionExpired} onClose={closeSecurityProfile} className="modules_security-profile" noButton>
      <SecurityProfile security={security} modelId={modelId} onClose={closeSecurityProfile} isPortfolioContext />
    </Modal>}
    {factorId && <Modal superModalOpened={sessionExpired} onClose={closeFactorProfile} className="modules_factor-profile" noButton>
      <FactorProfile portfolioId={portfolioId} factorId={factorId} modelId={modelId} onClose={closeFactorProfile} />
    </Modal>}
    <Dialog />
  </div>
}

PortfolioRoot.propTypes = {
  account: PropTypes.string.isRequired,
  portfolioId: PropTypes.string.isRequired,
  sidebar: PropTypes.element,
  security: PropTypes.shape({
    ticker: PropTypes.string.isRequired,
    exchange: PropTypes.string.isRequired,
  }),
  factorId: PropTypes.string,
  links: PropTypes.arrayOf(LINK).isRequired,
  subLinks: PropTypes.arrayOf(SUB_LINK),
  controls: CONTROLS.isRequired,
  modelId: PropTypes.string.isRequired,
  modelName: PropTypes.string.isRequired,
  portfolioName: PropTypes.string.isRequired,
  latestPortfolios: PropTypes.arrayOf(PORTFOLIO).isRequired,
  closeFactorProfile: PropTypes.func.isRequired,
  closeSecurityProfile: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  portfolioBenchmark: PropTypes.object.isRequired,
  selectedBase: PropTypes.object,
  setSelectedBase: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  baseInputFocus: PropTypes.func.isRequired,
  baseHistory: PropTypes.array.isRequired,
  searchResults: PropTypes.array.isRequired,
  baseInputBlur: PropTypes.func.isRequired,
  baseSearch: PropTypes.func.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  defaultSelectedBase: PropTypes.object.isRequired,
  portfolioHasData: PropTypes.bool.isRequired,
  navigationDisabled: PropTypes.bool.isRequired,
  loadActiveBase: PropTypes.func.isRequired,
  setModel: PropTypes.func.isRequired,
  modifiedScreens: PropTypes.array.isRequired,
  openDialog: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  hasEnoughBaseCoverage: PropTypes.bool,
  coverageNotification: PropTypes.func.isRequired,
  noRestrictionModels: PropTypes.array.isRequired,
  sessionExpired: PropTypes.bool,
  portfolioDates: PropTypes.shape({
    earliest: DATE,
    latest: DATE,
  }),
  menuItems: PropTypes.array,
  modelSupportsComposites: PropTypes.bool.isRequired,
  unsupportedCurrencyWarning: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  updateStateFromQueryString: PropTypes.func.isRequired,
}

// [TODO] make a hook
export default saveRecentResources(withPreferenceContext(PortfolioRoot, "portfolio"), "portfolioUUID")
